<template>
    <div class="enps-author-table">
        <VSpinner v-if="isLoading"></VSpinner>

        <div v-else-if="loadError" class="enps-author-table__empty">
            Не удалось загрузить список авторов
            <button type="button" class="enps-author-table__retry" @click="load">Повторить</button>
        </div>

        <template v-else>
            <EnpsTableToolbar
                placeholder="Поиск по ФИО"
                with-inactive-filter
                :hide-inactive="hideInactive"
                :visible-count="filteredSortedRows.length"
                :total-count="rows.length"
                @input="searchQuery = $event"
                @toggle-inactive="hideInactive = $event"
            ></EnpsTableToolbar>

            <div class="enps-author-table__row enps-author-table__row_header">
                <span
                    v-for="column in columns"
                    :key="column.key"
                    class="enps-author-table__cell"
                    :class="[
                        `enps-author-table__cell_${column.width}`,
                        { 'enps-author-table__cell_sortable': column.sortable },
                        { 'enps-author-table__cell_sorted': sortKey === column.key },
                    ]"
                    @click="column.sortable && sortBy(column.key)"
                >
                    {{ column.label }}
                    <span v-if="sortKey === column.key" class="enps-author-table__caret">{{ caret }}</span>
                </span>
            </div>

            <div
                v-for="author in filteredSortedRows"
                :key="author.accountId"
                class="enps-author-table__row"
                :class="{ 'enps-author-table__row_selected': selectedId === author.accountId }"
                @click="$emit('select-author', author)"
            >
                <!-- Уволенный сотрудник из истории оценок не исчезает — пометка объясняет
                     незнакомую фамилию, как и в рейтинге исполнителей. -->
                <span
                    class="enps-author-table__cell enps-author-table__cell_name"
                    :class="{ 'enps-author-table__cell_disabled': !author.enabled }"
                >
                    {{ author.displayName }}
                    <span v-if="!author.enabled" class="enps-author-table__badge" title="Аккаунт отключён">
                        неактивен
                    </span>
                </span>
                <span class="enps-author-table__cell enps-author-table__cell_rating">
                    {{ author.averageRating }}
                </span>
                <span class="enps-author-table__cell enps-author-table__cell_count">{{ author.ratingsCount }}</span>
                <span class="enps-author-table__cell enps-author-table__cell_count">{{ author.closedCount }}</span>
                <!-- Главная метрика автора: доходит ли он вообще до оценки. Прочерк — когда за
                     период у него не закрылось ни одного обращения (оценка могла прийти по
                     обращению, закрытому раньше), делить не на что. -->
                <span class="enps-author-table__cell enps-author-table__cell_count">
                    {{ author.ratedPercentage === null ? '—' : `${author.ratedPercentage}%` }}
                </span>
                <span class="enps-author-table__cell enps-author-table__cell_distribution">
                    <EnpsRatingDistributionBar :distribution="author.ratingDistribution"></EnpsRatingDistributionBar>
                </span>
            </div>

            <div v-if="rows.length === 0" class="enps-author-table__empty">Нет оценок за период</div>
            <div v-else-if="filteredSortedRows.length === 0" class="enps-author-table__empty">
                По запросу ничего не найдено
            </div>
        </template>
    </div>
</template>
<script>
// Components
import VSpinner from '@/views/components/VProgress/VSpinner.vue';
import EnpsRatingDistributionBar from './EnpsRatingDistributionBar.vue';
import EnpsTableToolbar from '@/views/components/EnpsTableToolbar.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import http from '@/js/http';
import { matchesQuery } from '@/js/textSearch';

// direction — направление ПЕРВОГО клика по колонке: у имени интереснее алфавит с начала, у
// всех числовых — сначала максимум.
const COLUMNS = [
    { key: 'displayName', label: 'Автор', width: 'name', sortable: true, direction: 'asc' },
    { key: 'averageRating', label: 'Средняя поставленная', width: 'rating', sortable: true, direction: 'desc' },
    { key: 'ratingsCount', label: 'Оценок', width: 'count', sortable: true, direction: 'desc' },
    { key: 'closedCount', label: 'Закрыто', width: 'count', sortable: true, direction: 'desc' },
    { key: 'ratedPercentage', label: 'Оценил', width: 'count', sortable: true, direction: 'desc' },
    { key: 'ratingDistribution', label: 'Распределение', width: 'distribution', sortable: false },
];

// Список авторов, ставящих оценки. Данные грузит сам (как EnpsMonthlyTrendChart со своим
// эндпоинтом), а не через Vuex-модуль дашборда: вкладка админская и открывается редко, тащить
// её в общую сводку — значит считать эти агрегаты каждому исполнителю на каждой загрузке.
//
// В отличие от рейтинга исполнителей сортировка здесь пользовательская: «лучший автор» —
// бессмысленное упорядочивание, поэтому вместо одного правильного порядка даётся выбор, а
// дефолт (по числу оценок, приходит с бэкенда) отвечает на вопрос «кто вообще оценивает».
@Component({
    components: {
        VSpinner,
        EnpsRatingDistributionBar,
        EnpsTableToolbar,
    },
})
class EnpsAuthorTable extends Vue {
    @Prop({ type: String, default: '' })
    period;

    // Кто открыт в панели справа — строка подсвечивается, как в рейтинге исполнителей.
    @Prop({ type: Number, default: null })
    selectedId;

    columns = COLUMNS;
    rows = [];
    isLoading = false;
    loadError = false;
    loadSeq = 0;

    sortKey = 'ratingsCount';
    sortDirection = 'desc';

    // Поиск/фильтр — состояние самой таблицы: чисто отображаемый срез уже загруженных rows,
    // ввод в поле не должен трогать ни сортировку, ни выбранного автора справа (см.
    // filteredSortedRows и комментарий в load() про единственное исключение).
    searchQuery = '';
    hideInactive = false;

    get caret() {
        return this.sortDirection === 'asc' ? '▲' : '▼';
    }

    get sortedRows() {
        const key = this.sortKey;
        const sign = this.sortDirection === 'asc' ? 1 : -1;

        // Копия: sort мутирует массив, а rows — данные ответа, и пересортировка не должна
        // зависеть от того, в каком порядке их оставил предыдущий клик.
        return this.rows.slice().sort((a, b) => {
            // Пустые значения разводятся ДО умножения на направление — см. compareNullability.
            const nullOrder = this.compareNullability(a[key], b[key]);

            if (nullOrder !== 0) {
                return nullOrder;
            }

            const compared = this.compareValues(a[key], b[key]) * sign;

            // Тай-брейк по имени всегда по алфавиту, независимо от направления: иначе строки с
            // одинаковым числом оценок перетасовывались бы при каждом переключении.
            return compared !== 0 ? compared : this.compareValues(a.displayName, b.displayName);
        });
    }

    // Фильтр — поверх уже отсортированного списка (порядок сортировки не должен меняться от
    // того, что часть строк временно скрыта). ВАЖНО: ничего в этом геттере не эмитит
    // 'select-author' — фильтрация только скрывает строки, выбранная в панели справа карточка
    // не переключается сама по себе (иначе EnpsRatingsPanel перегружала бы данные на каждое
    // нажатие клавиши в поиске). Единственное место, где filteredSortedRows[0] становится новым
    // выбором — load() ниже, и только там.
    get filteredSortedRows() {
        return this.sortedRows.filter((author) => {
            if (this.hideInactive && !author.enabled) {
                return false;
            }

            return matchesQuery(author.displayName, this.searchQuery);
        });
    }

    // «Нет значения» (доля оценённых у автора, у которого за период ничего не закрылось) всегда
    // в конце — и по возрастанию, и по убыванию: это не ноль, и всплывать наверх в сортировке
    // по возрастанию ему незачем. Отсюда отдельный шаг до применения направления: внутри
    // общего сравнения знак развернул бы и его.
    compareNullability(a, b) {
        const aEmpty = a === null || a === undefined;
        const bEmpty = b === null || b === undefined;

        if (aEmpty === bEmpty) {
            return 0;
        }

        return aEmpty ? 1 : -1;
    }

    compareValues(a, b) {
        if (a === b) {
            return 0;
        }

        return a > b ? 1 : -1;
    }

    sortBy(key) {
        if (this.sortKey === key) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            return;
        }

        this.sortKey = key;
        this.sortDirection = COLUMNS.find((column) => column.key === key).direction;
    }

    @Watch('period')
    onPeriodChange() {
        this.load();
    }

    load() {
        // Тот же счётчик от гонок, что в EnpsRatingsPanel: при быстром переключении периода
        // применяется только последний ответ.
        const seq = ++this.loadSeq;

        this.isLoading = true;
        this.loadError = false;

        http.get('/enps/dashboard/authors', { params: { period: this.period } })
            .then(({ data }) => {
                if (seq !== this.loadSeq) {
                    return;
                }

                this.rows = data;
                // Панель справа без выбранного автора — пустая заглушка, поэтому список сам
                // открывает первую ВИДИМУЮ строку (filteredSortedRows, а не sortedRows/data):
                // если поиск/чекбокс уже что-то скрывают на момент (пере)загрузки, панель не
                // должна открыть строку, которой не видно на экране. Это единственное место,
                // где фильтр разрешено использовать для выбора — обычный ввод в поиск сюда не
                // ведёт (см. комментарий у filteredSortedRows).
                this.$emit('select-author', this.filteredSortedRows[0] ?? null);
            })
            .catch(() => {
                if (seq !== this.loadSeq) {
                    return;
                }

                this.rows = [];
                this.loadError = true;
                this.$emit('select-author', null);
            })
            .finally(() => {
                if (seq === this.loadSeq) {
                    this.isLoading = false;
                }
            });
    }

    created() {
        this.load();
    }
}

export default EnpsAuthorTable;
</script>
<style lang="scss">
.enps-author-table {
    &__row {
        display: flex;
        padding: 10px 15px;
        cursor: pointer;
        border-bottom: 1px solid #e4eaef;

        &:hover {
            background-color: #f8f8f8;
        }

        // Полутон акцента (ACCENT_TINT из enpsColors.js).
        &_selected,
        &_selected:hover {
            background-color: #fdeadd;
        }

        &_header {
            font-weight: 600;
            cursor: default;

            &:hover {
                background-color: transparent;
            }
        }
    }

    &__cell {
        flex-shrink: 0;

        &_name {
            flex: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &_rating {
            width: 160px;
            text-align: right;
        }

        &_count {
            width: 80px;
            text-align: right;
        }

        &_distribution {
            display: flex;
            width: 120px;
            justify-content: flex-end;
        }

        &_disabled {
            color: #767676;
        }

        // Кликабельный заголовок: курсор и подсветка — единственный намёк, что колонку можно
        // пересортировать (у рейтинга исполнителей сортировки нет, и заголовки там мёртвые).
        &_sortable {
            cursor: pointer;
            user-select: none;

            &:hover {
                color: #c05a1f;
            }
        }

        &_sorted {
            color: #c05a1f;
        }
    }

    &__caret {
        margin-left: 4px;
        font-size: 0.625rem;
    }

    &__badge {
        margin-left: 6px;
        color: #a0a0a0;
        font-size: 0.75rem;
    }

    &__empty {
        padding: 20px 15px;
        text-align: center;
        color: #a0a0a0;
    }

    &__retry {
        display: block;
        margin: 8px auto 0;
        padding: 0;
        border: none;
        background: none;
        color: #c05a1f;
        font-size: 0.9375rem;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
}
</style>
